import { api } from '../api.js';

// Baked in at build time (esbuild `define`). Falls back to 'dev' for a raw
// dev bundle. Bumps every release deploy — the badge below is the at-a-glance
// "is my latest deploy live?" check.
declare const __APP_VERSION__: string;
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';

type LoginState = 'email' | 'checking' | 'login' | 'register';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K, styles?: string, attrs?: Record<string, string>
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (styles) node.style.cssText = styles;
  if (attrs) for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  return node;
}

function makeInput(type: string, placeholder: string, autocomplete: AutoFill): HTMLInputElement {
  const input = el('input', `
    width: 100%; background: var(--paper);
    border: 1px solid var(--line); border-radius: 8px;
    padding: 0.75rem; color: var(--ink); font-size: 1rem;
    outline: none; box-sizing: border-box; font-family: inherit;
  `);
  input.type = type;
  input.placeholder = placeholder;
  input.autocomplete = autocomplete;
  input.addEventListener('focus', () => { input.style.borderColor = 'var(--up)'; });
  input.addEventListener('blur', () => { input.style.borderColor = 'var(--line)'; });
  return input;
}

function makeButton(text: string): HTMLButtonElement {
  const btn = el('button', `
    width: 100%; background: var(--up); color: #fff;
    border: none; border-radius: 8px; padding: 0.85rem;
    font-size: 1rem; font-weight: 600; cursor: pointer;
    font-family: inherit;
  `);
  btn.type = 'submit';
  btn.textContent = text;
  return btn;
}

export function loginScreen(): HTMLElement {
  let state: LoginState = 'email';
  let currentEmail = '';

  // ── Root ────────────────────────────────────────────────────────
  const wrapper = el('div', `
    display: flex; align-items: center; justify-content: center;
    height: 100%; overflow: hidden; padding: 1.5rem;
    background: var(--bg);
  `);

  const container = el('div', `
    width: 100%; max-width: 360px;
  `);

  // ── Logo ────────────────────────────────────────────────────────
  const logoDiv = el('div', 'text-align: center; margin-bottom: 1.5rem;');

  const iconSpan = el('span', `
    font-size: 2.5rem; display: block; color: var(--up);
    font-family: 'SF Mono', 'Fira Code', monospace; font-weight: 700;
  `);
  iconSpan.textContent = '$';

  const logoTitle = el('h1', `
    font-size: 1.5rem; font-weight: 700; color: var(--ink); margin: 0.25rem 0 0;
  `);
  logoTitle.textContent = 'CoinHealth';

  const tagline = el('p', `
    font-size: 0.85rem; color: var(--muted); margin-top: 0.25rem;
  `);
  tagline.textContent = 'Your personal finance tracker';

  logoDiv.append(iconSpan, logoTitle, tagline);

  // ── Email field ─────────────────────────────────────────────────
  const emailSection = el('div', 'margin-bottom: 1rem;');

  const emailLabel = el('label', `
    display: block; color: var(--muted); font-size: 0.8rem;
    margin-bottom: 0.35rem; font-weight: 500;
  `);
  emailLabel.textContent = 'Email';

  const emailInput = makeInput('email', 'you@example.com', 'email');
  emailSection.append(emailLabel, emailInput);

  // ── Continue button ─────────────────────────────────────────────
  const continueBtn = makeButton('Continue');
  continueBtn.style.marginTop = '0.5rem';

  // ── Error message ───────────────────────────────────────────────
  const errorDiv = el('div', `
    background: var(--warn-bg); color: var(--down);
    border-radius: 8px; padding: 0.75rem; font-size: 0.875rem;
    text-align: center; display: none; margin-top: 0.75rem;
  `);

  // ── Password section (login mode) ──────────────────────────────
  const passwordSection = el('div', 'display: none; margin-top: 1rem;');

  const passwordLabel = el('label', `
    display: block; color: var(--muted); font-size: 0.8rem;
    margin-bottom: 0.35rem; font-weight: 500;
  `);
  passwordLabel.textContent = 'Password';

  const passwordInput = makeInput('password', 'Enter password', 'current-password');
  const loginBtn = makeButton('Sign In');
  loginBtn.style.marginTop = '1rem';

  const notYouLink = el('button', `
    background: none; border: none; color: var(--up);
    font-size: 0.85rem; cursor: pointer; margin-top: 0.75rem;
    display: block; width: 100%; text-align: center;
    font-family: inherit;
  `);
  notYouLink.textContent = 'Not you? Use a different email';

  passwordSection.append(passwordLabel, passwordInput, loginBtn, notYouLink);

  // ── Register section ───────────────────────────────────────────
  const registerSection = el('div', 'display: none; margin-top: 1rem;');

  const regPasswordLabel = el('label', `
    display: block; color: var(--muted); font-size: 0.8rem;
    margin-bottom: 0.35rem; font-weight: 500;
  `);
  regPasswordLabel.textContent = 'Choose a password';

  const regPasswordInput = makeInput('password', 'Min 8 characters', 'new-password');

  const confirmLabel = el('label', `
    display: block; color: var(--muted); font-size: 0.8rem;
    margin-bottom: 0.35rem; margin-top: 0.75rem; font-weight: 500;
  `);
  confirmLabel.textContent = 'Confirm password';

  const confirmInput = makeInput('password', 'Confirm password', 'new-password');
  const registerBtn = makeButton('Create Account');
  registerBtn.style.marginTop = '1rem';

  const alreadyLink = el('button', `
    background: none; border: none; color: var(--up);
    font-size: 0.85rem; cursor: pointer; margin-top: 0.75rem;
    display: block; width: 100%; text-align: center;
    font-family: inherit;
  `);
  alreadyLink.textContent = 'Already have an account? Sign in';

  registerSection.append(regPasswordLabel, regPasswordInput, confirmLabel, confirmInput, registerBtn, alreadyLink);

  // ── Version badge ───────────────────────────────────────────────
  const versionBadge = el('div', `
    text-align: center; color: var(--faint); font-size: 0.7rem;
    margin-top: 1.5rem; font-family: 'SF Mono', 'Fira Code', monospace;
    letter-spacing: 0.02em;
  `);
  versionBadge.textContent = `v${APP_VERSION}`;

  // ── Assemble ───────────────────────────────────────────────────
  container.append(
    logoDiv, emailSection, continueBtn,
    errorDiv, passwordSection, registerSection, versionBadge
  );
  wrapper.appendChild(container);

  // ── Helpers ─────────────────────────────────────────────────────

  function showError(msg: string): void {
    errorDiv.textContent = msg;
    errorDiv.style.display = 'block';
  }

  function hideError(): void {
    errorDiv.style.display = 'none';
  }

  function enterPhase2(): void {
    continueBtn.style.display = 'none';
    emailInput.readOnly = true;
    tagline.style.display = 'none';
  }

  function exitPhase2(): void {
    continueBtn.style.display = '';
    continueBtn.textContent = 'Continue';
    emailInput.readOnly = false;
    tagline.style.display = '';
    passwordSection.style.display = 'none';
    registerSection.style.display = 'none';
    passwordInput.value = '';
    regPasswordInput.value = '';
    confirmInput.value = '';
  }

  function showLoginMode(): void {
    state = 'login';
    enterPhase2();
    passwordSection.style.display = 'block';
    requestAnimationFrame(() => passwordInput.focus());
  }

  function showRegisterMode(): void {
    state = 'register';
    enterPhase2();
    registerSection.style.display = 'block';
    requestAnimationFrame(() => regPasswordInput.focus());
  }

  function goBackToEmail(): void {
    hideError();
    exitPhase2();
    state = 'email';
    emailInput.focus();
  }

  // ── Event handlers ─────────────────────────────────────────────

  async function handleEmailSubmit(): Promise<void> {
    const email = emailInput.value.trim();
    if (!email) {
      emailInput.style.borderColor = 'var(--down)';
      return;
    }

    currentEmail = email;
    state = 'checking';
    hideError();
    continueBtn.textContent = 'Checking...';
    continueBtn.disabled = true;

    try {
      const result = await api.post<{ exists: boolean }>('/check-user', { email });
      if (state !== 'checking') return;

      if (result.exists) {
        showLoginMode();
      } else {
        showRegisterMode();
      }
    } catch (err) {
      continueBtn.textContent = 'Continue';
      continueBtn.disabled = false;
      state = 'email';
      showError(err instanceof Error ? err.message : 'Network error. Please try again.');
    }
  }

  async function handleLogin(): Promise<void> {
    const password = passwordInput.value;
    if (!password) {
      passwordInput.style.borderColor = 'var(--down)';
      return;
    }

    hideError();
    loginBtn.disabled = true;
    loginBtn.textContent = 'Signing in...';

    try {
      await api.post('/login', { email: currentEmail, password });
      location.hash = '#home';
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Login failed');
      passwordInput.style.borderColor = 'var(--down)';
    } finally {
      loginBtn.disabled = false;
      loginBtn.textContent = 'Sign In';
    }
  }

  async function handleRegister(): Promise<void> {
    const password = regPasswordInput.value;
    const confirm = confirmInput.value;

    if (!password) {
      regPasswordInput.style.borderColor = 'var(--down)';
      return;
    }
    if (password.length < 8) {
      regPasswordInput.style.borderColor = 'var(--down)';
      showError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      confirmInput.style.borderColor = 'var(--down)';
      showError('Passwords do not match');
      return;
    }

    hideError();
    registerBtn.disabled = true;
    registerBtn.textContent = 'Creating account...';

    try {
      await api.post('/register', { email: currentEmail, password });
      location.hash = '#home';
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Registration failed';
      if (msg.includes('already in use') || msg.includes('409')) {
        exitPhase2();
        showLoginMode();
        showError('Account already exists. Please sign in.');
      } else {
        showError(msg);
      }
    } finally {
      registerBtn.disabled = false;
      registerBtn.textContent = 'Create Account';
    }
  }

  // ── Wire events ────────────────────────────────────────────────

  continueBtn.addEventListener('click', (e) => {
    e.preventDefault();
    handleEmailSubmit();
  });

  emailInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEmailSubmit();
    }
  });

  loginBtn.addEventListener('click', (e) => {
    e.preventDefault();
    handleLogin();
  });

  passwordInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLogin();
    }
  });

  registerBtn.addEventListener('click', (e) => {
    e.preventDefault();
    handleRegister();
  });

  confirmInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleRegister();
    }
  });

  notYouLink.addEventListener('click', goBackToEmail);
  alreadyLink.addEventListener('click', goBackToEmail);

  // Auto-focus email
  requestAnimationFrame(() => emailInput.focus());

  return wrapper;
}
